import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { ShieldCheck, AlertTriangle } from "lucide-react";

const Compliance = () => {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/compliance").then(({ data }) => {
      setItems(data);
      setActive(data[0]);
    });
  }, []);

  const colorOf = (v) => v >= 90 ? "emerald" : v >= 75 ? "amber" : "rose";

  return (
    <div className="p-6 max-w-7xl">
      <PageHeader
        tag="Governance / Compliance"
        title="Compliance Coverage"
        subtitle="Continuous alignment to ISO 27001, NIST CSF, PCI-DSS, SOC 2, HIPAA, CIS Benchmarks."
      />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {items.map((c) => {
          const col = colorOf(c.coverage);
          return (
            <button
              key={c.framework}
              data-testid={`framework-${c.framework.replace(/[^A-Za-z0-9]/g, "-")}`}
              onClick={() => setActive(c)}
              className={[
                "csx-card p-4 text-left transition-all",
                active?.framework === c.framework ? "border-cyan-500/70 shadow-[0_0_18px_rgba(6,182,212,0.2)]" : "",
              ].join(" ")}
            >
              <div className={`font-display text-3xl font-black text-${col}-400`}>{c.coverage}%</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">{c.framework}</div>
              <div className="mt-2 h-1 bg-slate-800">
                <div className={`h-full bg-${col}-500`} style={{ width: `${c.coverage}%` }} />
              </div>
            </button>
          );
        })}
      </div>

      {active && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="csx-card p-5 lg:col-span-2">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-cyan-400">{active.framework}</div>
                <h3 className="font-display text-xl mt-1">Audit Detail</h3>
              </div>
              <div className="text-xs text-slate-400 uppercase tracking-widest">
                Last Audit · {new Date(active.last_audit).toLocaleDateString()}
              </div>
            </div>
            <p className="text-sm text-slate-300 mt-4 leading-relaxed">{active.summary}</p>

            <div className="grid grid-cols-3 gap-3 mt-6">
              {[
                ["Controls Pass", active.controls_pass, "emerald"],
                ["Warn", active.controls_warn, "amber"],
                ["Fail", active.controls_fail, "rose"],
              ].map(([k, v, c]) => (
                <div key={k} className="border border-slate-800 p-3 bg-slate-950/40">
                  <div className={`font-display text-3xl font-black text-${c}-400`}>{v}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">{k}</div>
                </div>
              ))}
            </div>

            <div className="mt-5 pt-5 border-t border-slate-800">
              <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400 mb-2">Top Gaps (Sample)</div>
              <ul className="text-xs text-slate-300 space-y-1.5">
                <li className="flex gap-2"><AlertTriangle className="h-3 w-3 text-amber-400 mt-0.5" />Asset inventory cycle &gt; 30 days on 6 segments</li>
                <li className="flex gap-2"><AlertTriangle className="h-3 w-3 text-amber-400 mt-0.5" />Quarterly vulnerability scan retention incomplete</li>
                <li className="flex gap-2"><AlertTriangle className="h-3 w-3 text-rose-400 mt-0.5" />Vendor risk assessment overdue for 3 sub-processors</li>
              </ul>
            </div>
          </div>

          <div className="csx-card p-5">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-slate-400 mb-3">
              <ShieldCheck className="h-3 w-3 text-cyan-400" /> Coverage Trend (90d)
            </div>
            <div className="space-y-3">
              {items.map((c) => {
                const col = colorOf(c.coverage);
                return (
                  <div key={c.framework}>
                    <div className="flex justify-between text-[11px] mb-1">
                      <span className="text-slate-300">{c.framework}</span>
                      <span className={`text-${col}-400`}>{c.coverage}%</span>
                    </div>
                    <div className="h-1.5 bg-slate-800">
                      <div className={`h-full bg-${col}-500`} style={{ width: `${c.coverage}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Compliance;
